import { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import {
  computeBootcampEndIso,
  createBootcamp,
  fetchActiveCoursesWithLessons,
  fetchLessonCountForCourse,
  formatBootcampDate,
  loadClientTeamsWithBootcamps,
} from '../utils/bootcampScheduling';

function BootcampScheduleSection({ onBootcampChanged }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [scheduleClientId, setScheduleClientId] = useState('');
  const [scheduleCourseId, setScheduleCourseId] = useState('');
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsError, setTeamsError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [modalTeam, setModalTeam] = useState(null);
  const [bootcampStart, setBootcampStart] = useState('');
  const [bootcampStutter, setBootcampStutter] = useState('1');
  const [bootcampLessons, setBootcampLessons] = useState(0);
  const [bootcampEnd, setBootcampEnd] = useState('');
  const [bootcampError, setBootcampError] = useState('');
  const [bootcampLoading, setBootcampLoading] = useState(false);
  const [openRegions, setOpenRegions] = useState({});

  useEffect(() => {
    let mounted = true;
    setClientsLoading(true);
    supabase
      .from('clients')
      .select('id, business_name')
      .order('business_name')
      .then(({ data, error }) => {
        if (!mounted) return;
        setClients(error ? [] : (data || []));
        setClientsLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setCoursesLoading(true);
    fetchActiveCoursesWithLessons()
      .then((data) => {
        if (mounted) setCourses(data);
      })
      .catch(() => {
        if (mounted) setCourses([]);
      })
      .finally(() => {
        if (mounted) setCoursesLoading(false);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!scheduleClientId) {
      setTeams([]);
      setTeamsError('');
      setTeamsLoading(false);
      setOpenRegions({});
      return;
    }
    let mounted = true;
    setTeamsLoading(true);
    setTeamsError('');
    loadClientTeamsWithBootcamps(scheduleClientId)
      .then((data) => {
        if (mounted) setTeams(data);
      })
      .catch(() => {
        if (mounted) {
          setTeams([]);
          setTeamsError('Could not load teams');
        }
      })
      .finally(() => {
        if (mounted) setTeamsLoading(false);
      });
    return () => { mounted = false; };
  }, [scheduleClientId]);

  useEffect(() => {
    if (!scheduleCourseId) {
      setBootcampLessons(0);
      return;
    }
    let mounted = true;
    fetchLessonCountForCourse(scheduleCourseId)
      .then((count) => {
        if (mounted) setBootcampLessons(count);
      })
      .catch(() => {
        if (mounted) setBootcampLessons(0);
      });
    return () => { mounted = false; };
  }, [scheduleCourseId]);

  useEffect(() => {
    setBootcampEnd(computeBootcampEndIso(bootcampStart, bootcampStutter, bootcampLessons));
  }, [bootcampStart, bootcampStutter, bootcampLessons]);

  const selectedCourse = useMemo(
    () => courses.find((c) => String(c.id) === String(scheduleCourseId)),
    [courses, scheduleCourseId]
  );

  const teamsByRegion = useMemo(() => {
    const groups = new Map();
    teams.forEach((team) => {
      const regionKey = String(team.region_id || 'unassigned');
      const regionName = team.regions?.name || 'Unassigned';
      if (!groups.has(regionKey)) {
        groups.set(regionKey, { regionName, teams: [] });
      }
      groups.get(regionKey).teams.push(team);
    });
    return Array.from(groups.entries()).sort(([, a], [, b]) =>
      a.regionName.localeCompare(b.regionName)
    );
  }, [teams]);

  const toggleRegion = (regionKey) => {
    setOpenRegions((prev) => ({ ...prev, [regionKey]: !prev[regionKey] }));
  };

  const reloadTeams = async () => {
    if (!scheduleClientId) return;
    try {
      const data = await loadClientTeamsWithBootcamps(scheduleClientId);
      setTeams(data);
    } catch {
      setTeamsError('Could not load teams');
    }
  };

  const openScheduleModal = (team) => {
    setModalTeam(team);
    setBootcampStart('');
    setBootcampStutter('1');
    setBootcampError('');
    setShowModal(true);
  };

  const closeScheduleModal = () => {
    setShowModal(false);
    setModalTeam(null);
    setBootcampStart('');
    setBootcampStutter('1');
    setBootcampError('');
  };

  const handleCreateBootcamp = async () => {
    setBootcampError('');
    const stutterInt = parseInt(bootcampStutter, 10);
    if (!bootcampStart) {
      setBootcampError('Start date required');
      return;
    }
    if (Number.isNaN(stutterInt) || stutterInt < 1) {
      setBootcampError('Stutter time must be at least 1 day');
      return;
    }
    if (!scheduleCourseId) {
      setBootcampError('Course required');
      return;
    }
    if (!bootcampLessons) {
      setBootcampError('Selected course has no lessons');
      return;
    }
    if (!modalTeam) return;

    setBootcampLoading(true);
    const { error: insertError } = await createBootcamp({
      teamId: modalTeam.id,
      courseId: scheduleCourseId,
      startDate: bootcampStart,
      stutterDays: bootcampStutter,
      endDateIso: bootcampEnd,
    });
    setBootcampLoading(false);

    if (insertError) {
      setBootcampError(`Failed to create bootcamp: ${insertError.message}`);
      return;
    }

    closeScheduleModal();
    await reloadTeams();
    onBootcampChanged?.();
  };

  const renderTeamRow = (team) => {
    const scheduledBootcamp = team.activeBootcamp || team.upcomingBootcamp;
    const isActive = !!team.activeBootcamp;

    return (
      <div key={team.id} className="bootcamp-schedule-team-row">
        <div className="bootcamp-schedule-team-main">
          <div className="bootcamp-schedule-team-name">{team.name || 'Untitled Team'}</div>
          {scheduledBootcamp ? (
            <div className={`bootcamp-schedule-team-status ${isActive ? 'is-active' : 'is-upcoming'}`}>
              {isActive ? 'Active' : 'Upcoming'} Bootcamp: {scheduledBootcamp.courses?.name || 'Unknown Course'}
            </div>
          ) : null}
          {scheduledBootcamp ? (
            <div className="bootcamp-schedule-team-meta">
              {isActive ? 'Started' : 'Starts'}: {formatBootcampDate(scheduledBootcamp.start_date)}
              {' · '}
              Ends: {formatBootcampDate(scheduledBootcamp.end_date)}
            </div>
          ) : (
            <div className="bootcamp-schedule-team-meta">
              No bootcamp scheduled
              {selectedCourse ? ` for ${selectedCourse.name}` : ''}
            </div>
          )}
        </div>
        <div className="bootcamp-schedule-team-actions">
          {scheduledBootcamp ? (
            <button
              type="button"
              className="modern-button modern-button-secondary bootcamp-schedule-btn-overview"
              onClick={() => navigate(`/bootcamp/${scheduledBootcamp.id}`, { state: { userCount: team.userCount } })}
            >
              Bootcamp Overview
            </button>
          ) : (
            <button
              type="button"
              className="modern-button modern-button-success bootcamp-schedule-btn-schedule"
              disabled={!scheduleCourseId}
              onClick={() => openScheduleModal(team)}
            >
              Schedule Bootcamp
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderTeamsPanel = () => {
    if (!scheduleClientId || !scheduleCourseId) {
      return (
        <div className="modern-card bootcamp-schedule-teams-list">
          <div className="bootcamp-schedule-teams-placeholder">
            Select a client and course to view teams and schedule bootcamps.
          </div>
        </div>
      );
    }
    if (teamsLoading) {
      return (
        <div className="modern-card bootcamp-schedule-teams-list">
          <div className="modern-loading bootcamp-schedule-teams-placeholder">Loading teams…</div>
        </div>
      );
    }
    if (teamsError) {
      return (
        <div className="modern-card bootcamp-schedule-teams-list">
          <div className="modern-error bootcamp-schedule-teams-placeholder">{teamsError}</div>
        </div>
      );
    }
    if (teams.length === 0) {
      return (
        <div className="modern-card bootcamp-schedule-teams-list">
          <div className="bootcamp-schedule-teams-placeholder">
            No active teams found for this client.
          </div>
        </div>
      );
    }
    return (
      <div className="modern-card bootcamp-schedule-teams-list">
        {teamsByRegion.map(([regionKey, { regionName, teams: regionTeams }]) => {
          const isOpen = !!openRegions[regionKey];
          return (
            <div key={regionKey} className={`bootcamp-schedule-region-group${isOpen ? ' is-open' : ''}`}>
              <button
                type="button"
                className="bootcamp-schedule-region-header"
                onClick={() => toggleRegion(regionKey)}
                aria-expanded={isOpen}
              >
                <span className="bootcamp-schedule-region-chevron" aria-hidden="true">
                  {isOpen ? '▼' : '▶'}
                </span>
                <span className="bootcamp-schedule-region-title">{regionName}</span>
                <span className="bootcamp-schedule-region-count">
                  {regionTeams.length} {regionTeams.length === 1 ? 'team' : 'teams'}
                </span>
              </button>
              {isOpen && (
                <div className="bootcamp-schedule-region-teams">
                  {regionTeams.map(renderTeamRow)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className="bootcamp-schedule-section">
      <div className="modern-mb-4">
        <h2 className="modern-heading" style={{ marginBottom: 4 }}>Schedule Bootcamps</h2>
        <p className="modern-subtitle" style={{ marginBottom: 0 }}>
          Select a client and course, then schedule bootcamps for teams without an active or upcoming run.
        </p>
      </div>

      <div className="modern-card bootcamp-schedule-filters-card modern-mb-4">
        <div className="bootcamp-schedule-filters">
          <div className="bootcamp-schedule-filter-field">
            <label htmlFor="bootcamp-schedule-client">Client</label>
            <select
              id="bootcamp-schedule-client"
              className="modern-select bootcamp-schedule-select"
              value={scheduleClientId}
              disabled={clientsLoading}
              onChange={(e) => setScheduleClientId(e.target.value)}
            >
              <option value="">{clientsLoading ? 'Loading clients…' : 'Select a client'}</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.business_name}</option>
              ))}
            </select>
          </div>
          <div className="bootcamp-schedule-filter-field">
            <label htmlFor="bootcamp-schedule-course">Course</label>
            <select
              id="bootcamp-schedule-course"
              className="modern-select bootcamp-schedule-select"
              value={scheduleCourseId}
              disabled={coursesLoading}
              onChange={(e) => setScheduleCourseId(e.target.value)}
            >
              <option value="">{coursesLoading ? 'Loading courses…' : 'Select a course'}</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bootcamp-schedule-teams-panel">
        {renderTeamsPanel()}
      </div>

      {showModal && modalTeam && (
        <div className="modern-modal-overlay">
          <div className="modern-modal">
            <button type="button" onClick={closeScheduleModal} className="modern-modal-close">&times;</button>
            <h2 className="modern-heading">Schedule Bootcamp</h2>
            <p style={{ color: '#64748b', marginBottom: 16 }}>
              {modalTeam.name} · {selectedCourse?.name || 'Course'}
            </p>
            <div className="modern-mb-4">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151' }}>Start Date:</label>
              <input
                type="date"
                value={bootcampStart}
                onChange={(e) => setBootcampStart(e.target.value)}
                className="modern-input"
              />
            </div>
            <div className="modern-mb-4">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151' }}>Stutter Time (days):</label>
              <input
                type="number"
                min={1}
                value={bootcampStutter}
                onChange={(e) => setBootcampStutter(e.target.value)}
                className="modern-input"
              />
              <div style={{ color: '#64748b', fontSize: 13, marginTop: 2 }}>Number of days between lessons</div>
            </div>
            <div className="modern-mb-4">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151' }}>Number of Lessons:</label>
              <div style={{ fontWeight: 500 }}>{bootcampLessons || '-'}</div>
            </div>
            <div className="modern-mb-6">
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500, color: '#374151' }}>End Date:</label>
              <div style={{ fontWeight: 500 }}>{bootcampEnd ? new Date(bootcampEnd).toLocaleDateString() : '-'}</div>
            </div>
            {bootcampError && <div className="modern-error">{bootcampError}</div>}
            <button
              type="button"
              onClick={handleCreateBootcamp}
              disabled={bootcampLoading}
              className="modern-button modern-button-success"
              style={{ width: '100%' }}
            >
              {bootcampLoading ? 'Creating…' : 'Create Bootcamp'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default memo(BootcampScheduleSection);
